import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Modal, Card, Descriptions, Typography, Spin, Input, Button } from 'antd'
import { EnvironmentOutlined } from '@ant-design/icons'
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api'

const { Text } = Typography

const containerStyle = {
  width: '100%',
  height: '500px',
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.15)'
}

const mapOptions = {
  zoomControl: true,
  streetViewControl: true,
  mapTypeControl: true,
  fullscreenControl: true,
  mapTypeId: 'satellite',
}

const defaultCenter = { lat: 37.7749, lng: -122.4194 }

const LocationPickerModal = ({ open, onClose, onLocationSelect, value, otherStops = [] }) => {
  const [mapCenter, setMapCenter] = useState(defaultCenter)
  const [selected, setSelected] = useState(null)
  const [address, setAddress] = useState(null)
  const [loadingAddress, setLoadingAddress] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const searchInputRef = useRef(null)
  const mapRef = useRef(null)

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places']
  })

  const getAddressFromLatLng = function (lat, lng) {
    setLoadingAddress(true)
    const geocoder = new window.google.maps.Geocoder()
    geocoder.geocode({ location: { lat: lat, lng: lng } }, function (
      results,
      status
    ) {
      if (status === 'OK' && results && results.length > 0) {
        const components = results[0].address_components
        function getComponent(types) {
          for (let i = 0; i < components.length; i++) {
            var matched = types.every(t => components[i].types.includes(t))
            if (matched) return components[i].long_name
          }
          return null
        }

        results[0].formatted_address && setAddress({
          town: getComponent(['sublocality']) || getComponent(['neighborhood']),
          full: results[0].formatted_address
        })
      }
      else {
        setAddress(null)
      }
      setLoadingAddress(false)
    })
  }

  const onMapClick = useCallback(function (event) {
    const coords = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    }
    setSelected(coords)
    getAddressFromLatLng(coords.lat, coords.lng)
  }, [])

  const handleConfirm = async function () {
    if (selected) {
      setConfirmLoading(true)
      try {
        await onLocationSelect(Object.assign({}, selected, address || {}))
      } catch (error) {
        console.error(error)
      } finally {
        setConfirmLoading(false)
      }
    }
  }
  useEffect(() => {
    if (open) {
      // Sync/Reset search input
      setSearchText((value && value.full) || '')

      // Always try to get user's current location to trigger the browser prompt
      navigator.geolocation.getCurrentPosition(
        pos => {
          const coords = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          }
          // Only auto-center on current location if no value is already set
          if (!value || !value.lat || !value.lng) {
            setMapCenter(coords)
          }
        },
        () => {
          if (!value || !value.lat || !value.lng) {
            setMapCenter(defaultCenter)
          }
        }
      )

      if (value && value.lat && value.lng) {
        setMapCenter({ lat: value.lat, lng: value.lng })
        setSelected(value)
        // If we have coords but no address info, fetch it
        if (!value.full) {
          getAddressFromLatLng(value.lat, value.lng)
        } else {
          setAddress({ full: value.full })
        }
      } else {
        setSelected(null)
        setAddress(null)
      }
    }
  }, [open, value, isLoaded])

  const onLoadMap = map => {
    mapRef.current = map

    const streetView = map.getStreetView()
    if (streetView) {
      const updateLocation = () => {
        if (streetView.getVisible()) {
          const pos = streetView.getPosition()
          if (pos) {
            const lat = pos.lat()
            const lng = pos.lng()
            const coords = { lat, lng }
            setSelected(coords)
            getAddressFromLatLng(lat, lng)
          }
        }
      }

      streetView.addListener('position_changed', updateLocation)
      streetView.addListener('visible_changed', updateLocation)
    }

    if (window.google && searchInputRef.current) {
      const input = searchInputRef.current.input
      const autocomplete = new window.google.maps.places.Autocomplete(input)
      autocomplete.bindTo('bounds', map)

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        if (!place.geometry || !place.geometry.location) return

        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()
        const coords = { lat, lng }
        setSelected(coords)
        if (place.formatted_address) {
          setAddress({
            town: place.name || place.formatted_address,
            full: place.formatted_address
          })
          setSearchText(place.formatted_address)
        }
        getAddressFromLatLng(lat, lng)
        map.panTo(coords)
      })
    }
  }

  return (
    <Modal
      title="📍 Select Location"
      visible={open}
      onCancel={onClose}
      onOk={handleConfirm}
      confirmLoading={confirmLoading}
      okText="Confirm"
      width={1000}
      okButtonProps={{ disabled: !selected }}
    >
      {isLoaded ? (
        <>
          <div style={{ display: 'flex', gap: '8px', marginBottom: 12 }}>
            <Input
              placeholder="Search location..."
              ref={searchInputRef}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={18}
            options={mapOptions}
            onClick={onMapClick}
            onLoad={onLoadMap}
          >
            {selected ? (
              <Marker
                position={selected}
                draggable={true}
                onDragEnd={(e) => {
                  const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() }
                  setSelected(coords)
                  getAddressFromLatLng(coords.lat, coords.lng)
                }}
              />
            ) : null}

            {otherStops.map(stop => {
                if (!stop.location || !stop.location.coordinates) return null;
                const coords = stop.location.coordinates.split(',');
                const lat = parseFloat(coords[0]);
                const lng = parseFloat(coords[1]);
                if (isNaN(lat) || isNaN(lng)) return null;

                return (
                    <Marker
                        key={stop.stop_number}
                        position={{ lat, lng }}
                        draggable={false}
                        label={{
                            text: String(stop.stop_number),
                            color: 'white',
                            fontWeight: 'bold',
                        }}
                    />
                )
            })}
          </GoogleMap>

          {selected ? (
            <Card
              title="Selected Location Info"
              style={{ marginTop: 16 }}
              bordered
              type="inner"
            >
              {loadingAddress ? (
                <Spin />
              ) : (
                <Descriptions column={1}>
                  <Descriptions.Item label="Latitude">
                    {selected.lat.toFixed(5)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Longitude">
                    {selected.lng.toFixed(5)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Town">
                    {(address && address.town) || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Full Address">
                    <Text type="secondary">
                      {(address && address.full) || 'N/A'}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>
              )}
            </Card>
          ) : null}
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Spin tip="Loading Map..." />
        </div>
      )}
    </Modal>
  )
}

export default LocationPickerModal
